import { Badge, Divider, Group, Stack, Table, Text } from "@mantine/core";
import isNil from "lodash/isNil";

import { PartJsonDto } from "#modules/parts/infra/fetchPartJson.api";
import type { GdtType, PartComplexity, PartGdt, PartRoughness, PartTolerance } from "shared";

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt={10} mb={2}>
      {children}
    </Text>
  );
}

const GDT_LABELS: Record<GdtType, string> = {
  perpendicularity: "Perpendicularité",
  flatness: "Planéité",
  symmetry: "Symétrie",
  parallelism: "Parallélisme",
  position: "Position",
  roundness: "Circularité",
  circular_runout: "Battement circulaire",
  cylindricity: "Cylindricité",
  angularity: "Angularité",
  profile_line: "Profil d'une ligne",
  straightness: "Rectitude",
  profile_plane: "Profil d'une surface",
  concentricity: "Concentricité",
  total_runout: "Battement total",
};

const GDT_SYMBOLS: Record<GdtType, string> = {
  perpendicularity: "⟂",
  flatness: "⏥",
  symmetry: "⌯",
  parallelism: "∥",
  position: "⌖",
  roundness: "⌖",
  circular_runout: "↗",
  cylindricity: "⌭",
  angularity: "∠",
  profile_line: "⌒",
  straightness: "⏤",
  profile_plane: "⌓",
  concentricity: "◎",
  total_runout: "⌰",
};

export function formatTolerance(tolerance: PartTolerance | null | undefined): string {
  return tolerance?.raw_value ?? "";
}

type AnyMeasure = NonNullable<PartComplexity["measures"]>[number];

const MEASURE_TYPE_LABELS: Record<AnyMeasure["type"], string> = {
  linear: "Longueurs",
  diameter: "Diamètres",
  radius: "Rayons",
  angle: "Angles",
  threading: "Taraudages",
};

function formatMeasureValue(measure: AnyMeasure): string {
  switch (measure.type) {
    case "linear":
      return `${measure.length} mm (${formatTolerance(measure.tolerance)})`;
    case "diameter":
      return `Ø${measure.diameter} (${formatTolerance(measure.tolerance)})`;
    case "radius":
      return `R${measure.radius} (${formatTolerance(measure.tolerance)})`;
    case "angle":
      return `${measure.angle}° (${formatTolerance(measure.tolerance)})`;
    case "threading":
      return `M${measure.diameter} (${formatTolerance(measure.tolerance)})`;
  }
}

function MeasuresSection({ complexity }: { complexity: PartComplexity }) {
  const measures = complexity.measures;
  if (!measures?.length) {
    return null;
  }

  const grouped = measures.reduce<Record<string, AnyMeasure[]>>(
    (acc, measure) => {
      if (!acc[measure.type]) {
        acc[measure.type] = [];
      }
      acc[measure.type]!.push(measure);
      return acc;
    },
    {} as Record<string, AnyMeasure[]>,
  );

  return (
    <>
      <SectionTitle>Côtes</SectionTitle>
      {Object.entries(grouped).map(([type, typeMeasures]) => (
        <div key={type}>
          <Text size="xs" c="dimmed" mt={6} mb={2}>
            {MEASURE_TYPE_LABELS[type as AnyMeasure["type"]]}
          </Text>
          <Group gap={4}>
            {typeMeasures.map((measure, i) => (
              <Badge key={i} size="xs" variant="light">
                {formatMeasureValue(measure)}
              </Badge>
            ))}
          </Group>
        </div>
      ))}
    </>
  );
}

function GdtsSection({ gdts }: { gdts: PartGdt[] }) {
  if (!gdts.length) {
    return null;
  }

  return (
    <>
      <SectionTitle>Contraintes géométriques</SectionTitle>
      <Table verticalSpacing={2} fz="sm" withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Text size="xs" c="dimmed">
                Type
              </Text>
            </Table.Th>
            <Table.Th>
              <Text size="xs" c="dimmed">
                Valeur
              </Text>
            </Table.Th>
            <Table.Th>
              <Text size="xs" c="dimmed">
                Référence
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {gdts.map((gdt, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                <Text size="xs">
                  {GDT_LABELS[gdt.type]} {GDT_SYMBOLS[gdt.type]}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" fw={500}>
                  {!isNil(gdt.tolerance) ? gdt.tolerance : "–"}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="xs" fw={500}>
                  {gdt.datum.length > 0 ? gdt.datum.join(", ") : "–"}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </>
  );
}

function RoughnessesSection({ roughnesses }: { roughnesses: PartRoughness[] }) {
  if (!roughnesses.length) {
    return null;
  }

  return (
    <>
      <SectionTitle>Rugosités</SectionTitle>
      <Group gap={4} mt={2}>
        {roughnesses.map((r, i) => (
          <Badge key={i} size="xs" variant="light">
            Ra {r.value}
          </Badge>
        ))}
      </Group>
    </>
  );
}

export function AnalysisResultComplexitySection({
  complexity,
}: {
  complexity: NonNullable<PartJsonDto>["complexity"];
}) {
  if (!complexity) {
    return null;
  }

  const hasMeasures = !!complexity.measures?.length;
  const hasGdts = !!complexity.gdts?.length;
  const hasRoughnesses = !!complexity.roughnesses?.length;

  if (!hasMeasures && !hasGdts && !hasRoughnesses) {
    return (
      <Text size="xs" c="dimmed" px="md">
        Aucune donnée disponible
      </Text>
    );
  }

  return (
    <Stack p="md" pt={0} gap={2}>
      {hasMeasures && <MeasuresSection complexity={complexity} />}
      {hasMeasures && (hasGdts || hasRoughnesses) && <Divider my={6} />}
      {hasGdts && <GdtsSection gdts={complexity.gdts!} />}
      {hasGdts && hasRoughnesses && <Divider my={6} />}
      {hasRoughnesses && <RoughnessesSection roughnesses={complexity.roughnesses!} />}
    </Stack>
  );
}
